export const SAFE_AREA_EVENT = "mt:safearea";

export type SafeAreaInsets = {
  top: number;
  right: number;
  bottom: number;
  left: number;
  seq: number;
};

const PROPERTIES = {
  top: "--safe-area-top",
  right: "--safe-area-right",
  bottom: "--safe-area-bottom",
  left: "--safe-area-left",
} as const;

function finiteInset(value: unknown): number | null {
  const number = Number(value);
  if (!Number.isFinite(number) || number < 0 || number > 256) return null;
  return Math.round(number * 100) / 100;
}

export function safeAreaFrom(event: Event): SafeAreaInsets | null {
  if (!(event instanceof CustomEvent) || !event.detail) return null;
  const detail = event.detail as Record<string, unknown>;
  const top = finiteInset(detail.top);
  const right = finiteInset(detail.right);
  const bottom = finiteInset(detail.bottom);
  const left = finiteInset(detail.left);
  const seq = Number(detail.seq);
  if (
    top === null ||
    right === null ||
    bottom === null ||
    left === null ||
    !Number.isSafeInteger(seq) ||
    seq < 0
  ) {
    return null;
  }
  return { top, right, bottom, left, seq };
}

/**
 * Mirrors the iOS key-window safe area into the single CSS token owner.
 * Browser and Android behavior stays unchanged because only the iOS shell
 * dispatches mt:safearea.
 */
export function installNativeSafeArea(
  target: Window = window,
  root: HTMLElement = document.documentElement,
): () => void {
  let latestSeq = -1;
  const apply = (event: Event) => {
    const safe = safeAreaFrom(event);
    if (!safe || safe.seq <= latestSeq) return;
    latestSeq = safe.seq;
    for (const edge of ["top", "right", "bottom", "left"] as const) {
      root.style.setProperty(PROPERTIES[edge], `${safe[edge]}px`);
    }
    root.dataset.safeAreaSeq = String(safe.seq);
  };
  target.addEventListener(SAFE_AREA_EVENT, apply);
  return () => target.removeEventListener(SAFE_AREA_EVENT, apply);
}
